import { motion } from 'framer-motion';
// material
import { styled } from '@mui/material/styles';
import { Button, Box, Container, Typography, Stack, Theme, useTheme, Grid } from '@mui/material';
//
import { varFadeInDown, varFadeInUp, MotionInView } from '../../animate';
import { ButtonContentProps } from '../../../@types/components-overview';
import { Link as RouterLink } from 'react-router-dom';
import { SxProps } from '@mui/system';

// ----------------------------------------------------------------------

interface ContentStyleProps {
  theme: Theme;
  showAnimation: boolean;
}

const ContentStyle = styled('div')(({ theme, showAnimation }: ContentStyleProps) => ({
  margin: 'auto',
  overflow: 'hidden',
  borderRadius: theme.shape.borderRadiusMd,
  backgroundImage: `linear-gradient(135deg,
    ${theme.palette.primary.main} 0%,
    ${theme.palette.primary.dark} 100%)`,
  [theme.breakpoints.up('md')]: {
    display: 'flex',
    maxWidth: '100%',
    paddingBottom: 0,
    alignItems: 'center'
  },
  height: '100%'
}));

// ----------------------------------------------------------------------
const RootStyle = styled('div')();

export interface LandingAdvertisementProps {
  showAnimation?: boolean;
  title: string;
  subtitle?: string;
  subtitleBold?: string;
  buttons: ButtonContentProps[];
  sx?: SxProps;
}
export default function LandingAdvertisement({
  showAnimation = true,
  title,
  subtitleBold,
  subtitle,
  buttons,
  sx
}: LandingAdvertisementProps) {
  const theme = useTheme();
  return (
    <RootStyle>
      <Container sx={{ ...sx, marginBottom: '5em' }} maxWidth="lg">
        <ContentStyle theme={theme} showAnimation={showAnimation}>
          <Grid container>
            {showAnimation && (
              <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
                <MotionInView
                  variants={varFadeInUp}
                  sx={{
                    mb: { xs: 3, md: 0 }
                  }}
                >
                  <motion.div
                    animate={{ y: [10, 0, 10] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <Box
                      component="img"
                      alt="rocket"
                      src="/static/icons/ibanIcons/IconTime.svg"
                      sx={{ margin: '10% auto', width: '60%' }}
                    />
                  </motion.div>
                </MotionInView>
              </Grid>
            )}
            <Grid
              item
              xl={showAnimation ? 6 : 12}
              lg={showAnimation ? 6 : 12}
              md={showAnimation ? 6 : 12}
              sm={12}
              xs={12}
            >
              <Box
                sx={{
                  padding: {
                    xl: '5em 0 ',
                    lg: '5em 0 ',
                    md: '5em 0 ',
                    sm: '1em 0 ',
                    xs: '1em 0 '
                  },
                  textAlign: { xs: 'center', md: 'left' }
                }}
              >
                <MotionInView variants={varFadeInDown} sx={{ color: 'common.white' }}>
                  <Container maxWidth={'md'}>
                    <Stack spacing={5}>
                      <Typography
                        sx={{
                          display: { lg: 'block', xl: 'block', sm: 'none', xs: 'none', md: 'none' }
                        }}
                        variant="h2"
                      >
                        {title}
                      </Typography>
                      <Typography sx={{ display: { xl: 'none', lg: 'none' } }} variant="h4">
                        {title}
                      </Typography>
                      {subtitle && <Typography>{subtitle}</Typography>}
                      {subtitleBold && (
                        <Typography sx={{ fontWeight: 'bold' }}>{subtitleBold}</Typography>
                      )}
                      <Stack
                        spacing={1}
                        direction={{ sm: 'column', xs: 'column', md: 'row', lg: 'row', xl: 'row' }}
                      >
                        {buttons.map((button, index) => (
                          <Button
                            key={index}
                            size={button.size}
                            variant={button.variant}
                            to={button.to}
                            color={button.color}
                            component={RouterLink}
                          >
                            {button.text}
                          </Button>
                        ))}
                      </Stack>
                    </Stack>
                  </Container>
                </MotionInView>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ margin: { sm: '3em 0', xs: '3em 0' } }} />
        </ContentStyle>
      </Container>
    </RootStyle>
  );
}
